import { cn, focusRing } from "@simpro/ui";
import { Fragment } from "react";
import { useSalonMoney } from "@/features/salon";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import { pill } from "@/shared/styles/pill";
import { useUnstaffedServices } from "../hooks/useUnstaffedServices";
import { formatDuration } from "../lib/formatDuration";
import { MAX_SERVICES, type PublicCatalog } from "../schemas/booking.schemas";
import { PlusCheck } from "./AddServiceButton";
import { CallToBook } from "./CallToBook";

/** What the salon wrote for its featured treatment (FR-SA-203), as the page passes it in. */
export interface FeaturedContent {
  serviceId: string | null;
  eyebrow: string | null;
  title: string | null;
  highlight: string | null;
  text: string | null;
  image: { url: string; altText: string } | null;
}

export interface FeaturedTreatmentProps {
  catalog: PublicCatalog;
  featured: FeaturedContent;
  /** In the order they were added, which is the order they are done in. */
  selected: readonly string[];
  onToggle: (serviceId: string) => void;
  /** The team band follows straight on, so this band's foot runs into it with no gap. */
  joined?: boolean;
}

/**
 * The featured treatment (TDD-SA FR-SA-203; design note salon-booking-organic-wellness.md section
 * 10.1): the board's "Ritual of the season" band. It is green, rising out of the cream on rounded
 * shoulders. The picture sits in a slowly morphing pebble, and beside it are the salon's small line,
 * the title in the display face with its highlighted words in italic in the soft accent, the text,
 * and a soft-accent pill that adds the treatment to the visit (and takes it out again) with its time
 * beside it.
 *
 * Every field the salon left blank is the service's own: its name, its description, its photo. With
 * no service chosen, or one no longer on the menu, there is no band.
 */
export function FeaturedTreatment({ catalog, featured, selected, onToggle, joined = false }: FeaturedTreatmentProps) {
  const money = useSalonMoney();
  const byPhone = useUnstaffedServices(featured.serviceId ? [featured.serviceId] : []);
  const service = catalog.flatMap((category) => category.services).find((item) => item.id === featured.serviceId);
  if (!service) return null;

  const title = featured.title ?? service.name;
  const text = featured.text ?? service.description;
  const picture = featured.image ?? (service.imageUrl ? { url: service.imageUrl, altText: "" } : null);
  const added = selected.includes(service.id);
  const full = selected.length >= MAX_SERVICES;

  return (
    <section aria-labelledby="featured-heading" className={cn("on-ink band-top bg-bar text-on-bar", joined && "pb-0")}>
      <div
        className={cn(
          page,
          "grid items-center gap-12 pt-20 md:pt-28 lg:gap-16 lg:pt-36",
          picture ? "lg:grid-cols-2" : "",
          joined ? "pb-20 md:pb-28" : "pb-20 md:pb-28 lg:pb-36",
        )}
      >
        {picture ? (
          <Reveal className="animate-morph mx-auto aspect-square w-full max-w-[34rem] overflow-hidden rounded-[48%_52%_46%_54%/54%_44%_56%_46%]">
            <img src={picture.url} alt={picture.altText} loading="lazy" className="size-full object-cover" />
          </Reveal>
        ) : null}
        <div className="flex flex-col gap-6">
          <Reveal as="p" className="text-sm font-medium text-on-bar-muted">
            {featured.eyebrow ?? "Featured treatment"}
          </Reveal>
          <Reveal as="h2" index={1} id="featured-heading" className="display-section text-on-bar">
            <Lit text={title} highlight={featured.title ? featured.highlight : null} />
          </Reveal>
          {text ? (
            <Reveal as="p" index={2} className="text-on-bar/85 max-w-[44ch] text-[17px] leading-[1.7] md:text-lg">
              {text}
            </Reveal>
          ) : null}
          <Reveal index={3} className="flex flex-wrap items-center gap-4">
            {byPhone.has(service.id) && !added ? (
              <CallToBook serviceName={service.name} onInk />
            ) : (
            <button
              type="button"
              aria-pressed={added}
              disabled={!added && full}
              onClick={() => onToggle(service.id)}
              className={cn(
                pill.base,
                "h-[3.25rem] gap-2 bg-ink-link px-7 text-base text-bar hover:-translate-y-0.5 hover:bg-on-bar",
                focusRing,
              )}
            >
              {added ? <PlusCheck on className="size-4" /> : null}
              {added ? "Added to my visit" : "Add to my visit"} · {money(service.price)}
            </button>
            )}
            <span className="text-[15px] text-on-bar-muted">{formatDuration(service.durationMinutes)}</span>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/** The title with the salon's highlighted words, else its last word, in italic in the soft accent. */
function Lit({ text, highlight }: { text: string; highlight: string | null }) {
  const trimmed = text.trim();
  const at = highlight ? trimmed.toLowerCase().indexOf(highlight.trim().toLowerCase()) : -1;
  const [from, to] =
    highlight && at >= 0 ? [at, at + highlight.trim().length] : [trimmed.lastIndexOf(" ") + 1, trimmed.length];
  if (from <= 0 && to >= trimmed.length) return <>{trimmed}</>;
  return (
    <Fragment>
      {trimmed.slice(0, from)}
      <em className="text-ink-link">{trimmed.slice(from, to)}</em>
      {trimmed.slice(to)}
    </Fragment>
  );
}
